import { useRouter } from 'next/router';
import { useState, useContext, useEffect, useMemo, useRef } from 'react';
import MartyLoginAnimation from 'components/about/animations/MartyLoginAnimation';
import SignupContent from 'components/login/SignupContent';
import LinkAccounts from 'components/login/LinkAccounts';
import { UserContext } from 'contexts/user';
import { useIsPlaywrightTest } from 'lib/deviceUtils';

import LoginPage from 'components/login/LoginPage';
import { navigateTo } from 'lib/utils';
import { sanitizeRedirect } from 'components/login/redirectUtils';
import * as analytics from 'lib/analytics';

export default function Signup() {
  const router = useRouter();
  const { user, userLoaded, cachedSignedOut } = useContext(UserContext);
  const isTest = useIsPlaywrightTest();

  const [serverError, setServerError] = useState<string | undefined>();

  const sessionId = useMemo(() => analytics.generateSessionId(), []);
  const viewedRef = useRef(false);
  const oauthErrorLoggedRef = useRef(false);
  const oauthError = user?.oauth?.error;
  const isLinkingAccounts =
    user?.oauth?.nm !== undefined && user?.oauth?.em !== undefined;
  const canShowAuthUi = userLoaded || cachedSignedOut === true;
  const shouldTrackSignupView =
    userLoaded && !user?.signedIn && !isLinkingAccounts;

  useEffect(() => {
    setServerError((error) => (error === undefined ? oauthError : error));
  }, [oauthError]);

  useEffect(() => {
    if (!shouldTrackSignupView) {
      return;
    }

    const redirect = sanitizeRedirect(router.query.redirect) || null;

    if (!viewedRef.current) {
      viewedRef.current = true;
      analytics.authPageViewed({
        session_id: sessionId,
        page: 'signup',
        referrer: document.referrer || null,
        redirect_target: redirect,
        is_linking_accounts: false,
        has_oauth_error: !!oauthError,
        oauth_error: oauthError ?? null,
      });
    }

    if (oauthError && !oauthErrorLoggedRef.current) {
      oauthErrorLoggedRef.current = true;
      analytics.signupOauthReturnedWithError({
        session_id: sessionId,
        provider: user?.oauth?.nm ?? null,
        error: oauthError,
      });
    }
  }, [
    oauthError,
    router.query.redirect,
    sessionId,
    shouldTrackSignupView,
    user,
  ]);

  if (userLoaded && user?.signedIn) {
    const redirect = sanitizeRedirect(router.query.redirect);
    if (redirect) {
      navigateTo(redirect);
    } else {
      navigateTo('/');
    }
    return <></>;
  }

  function signupContents() {
    if (!canShowAuthUi) {
      return null;
    } else if (isLinkingAccounts) {
      const { oauth } = user;
      return (
        <LinkAccounts
          email={oauth!.em}
          page="signup"
          provider={oauth!.nm}
          sessionId={sessionId}
        />
      );
    } else {
      return <SignupContent serverError={serverError} sessionId={sessionId} />;
    }
  }

  return (
    <LoginPage
      title="Sign up"
      animationComponent={!isTest ? <MartyLoginAnimation /> : undefined}
    >
      {signupContents()}
    </LoginPage>
  );
}
